import { SpatialDatasetProcessingStatus, SpatialDatasetSourceFormat, SpatialDatasetType } from "../types";

/** Chunk size for multipart uploads — backend rejects any other size. */
export const SPATIAL_DATASET_CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB exactly

/** Prefix for one map overlay per spatial dataset (`spatial-<id>`). */
export const SPATIAL_LAYER_ID_PREFIX = "spatial-";

/** @deprecated Use {@link spatialDatasetLayerId}. Kept so older stored ids still match. */
export const SPATIAL_ACTIVE_LAYER_ID = "spatial-active";

export const spatialDatasetLayerId = (id: number | string): string => `${SPATIAL_LAYER_ID_PREFIX}${id}`;

/**
 * MapLibre hands us `sourceId` straight off events like `sourcedata`, where a
 * style/metadata event can carry none at all, so the argument is treated as
 * untrusted rather than asking every call site to pre-check it.
 */
export const isSpatialDatasetLayerId = (id?: string | null): boolean => typeof id === "string" && id.startsWith(SPATIAL_LAYER_ID_PREFIX);

export const datasetIdFromSpatialLayerId = (layerId?: string | null): string | null =>
  isSpatialDatasetLayerId(layerId) ? (layerId as string).slice(SPATIAL_LAYER_ID_PREFIX.length) : null;

export const SPATIAL_DATASET_POLL_INTERVAL_MS = 3_000; // 3 s

/** Maps file extension (lower-cased, no dot) → source_format sent to the API. */
export const EXTENSION_TO_SOURCE_FORMAT: Record<string, SpatialDatasetSourceFormat> = {
  tif: "geotiff",
  tiff: "geotiff",
  gpx: "gpx",
  geojson: "geojson",
  json: "geojson",
  kml: "kml",
  kmz: "kml",
  csv: "csv",
  // .zip is the browser-visible extension for Shapefile (.shp.zip) bundles.
  // The backend expects a zip containing .shp + sidecars; source_format must be "shapefile".
  zip: "shapefile"
};

/** Dropzone accept map — extensions the spatial upload mode allows. */
export const SPATIAL_DATASET_ACCEPTED_FILES: Record<string, string[]> = {
  "image/tiff": [".tif", ".tiff"],
  "application/zip": [".zip"],
  "application/gpx+xml": [".gpx"],
  "application/geo+json": [".geojson", ".json"],
  "application/vnd.google-earth.kml+xml": [".kml", ".kmz"],
  "text/csv": [".csv"]
};

/** MIME type sent with the S3 presigned PUT for each source format. */
export const SOURCE_FORMAT_MIME: Record<SpatialDatasetSourceFormat, string> = {
  geotiff: "image/tiff",
  shapefile: "application/zip",
  gpx: "application/gpx+xml",
  geojson: "application/geo+json",
  kml: "application/vnd.google-earth.kml+xml",
  csv: "text/csv"
};

/**
 * Maps source_format → the dataset_type the backend derives from it.
 * Lets the upload table say Vector / Raster / Tabular before the file is sent —
 * the server-side `dataset_type` only exists once the dataset has been created.
 */
export const SOURCE_FORMAT_DATASET_TYPE: Record<SpatialDatasetSourceFormat, SpatialDatasetType> = {
  geotiff: "raster",
  shapefile: "vector",
  gpx: "vector",
  geojson: "vector",
  kml: "vector",
  csv: "tabular"
};

export const DATASET_TYPE_LABEL: Record<SpatialDatasetType, string> = {
  raster: "Raster",
  vector: "Vector",
  tabular: "Tabular"
};

export const SOURCE_FORMAT_LABEL: Record<SpatialDatasetSourceFormat, string> = {
  geotiff: "GeoTIFF",
  shapefile: "Shapefile (.shp.zip)",
  gpx: "GPX",
  geojson: "GeoJSON",
  kml: "KML / KMZ",
  csv: "CSV (tabular)"
};

/**
 * Maps processing_status → human-readable tile label.
 * Used by TileStatusOverlay and the DataListView status chip.
 */
export const SPATIAL_STATUS_TILE_LABEL: Record<SpatialDatasetProcessingStatus, string> = {
  pending: "Pending",
  uploading: "Uploading",
  queued: "Queued",
  processing: "Processing…",
  completed: "Completed",
  failed: "Processing Failed"
};
